'use client';
// Original Imports (ensure all needed are present)
import axios from "axios"; // Ensure axios is imported
import { RiAlarmWarningLine } from '@remixicon/react';
import { Callout, Button, Title, Text, Card } from '@tremor/react';
import React, { useState, useRef, useEffect } from "react";




import { useRouter } from "next/navigation";
import { useToast } from '@/components/ui/use-toast'; // Ensure toast is imported
import { UpdateLeadForm } from '@/app/(routes)/leads/components/UpdateLeadForm';
import RightViewModalNoTrigger from '@/components/modals/right-view-notrigger'; // Adjusted path assumption
import { Input } from '@/components/ui/input';

// Original Interfaces/Types
interface FunctionCallData {
  function: string;
  args: {
    toggle: string;
  };
}
type Lead = {
  businessFoundingDate: any;
  expirationDate: any;
  firstName: string;
  middleName: string;
  lastName: string;
  streetAddress: string;
  postalCode: string;
  ssn: string;
  phone: string;
  email: string;
  state: string;
  city: string;
  country: string;
  advisorName: string;
  advisorEmail: string;
  isReturningInvestor: any;
  ownsEquipmentCompany: any;
  dlfEngagementForLLC: any;
  preferredBusinessName: string;
  alternateBusinessName: string;
  stateOfFormation: string;
  equipmentUsagePlan: string;
  assetInterests?: string[];
  investmentAmount: number;
  investmentAmount2025?: number;
  downPaymentPercentage: number;
  businessType: string;
  percentageOwnership: number;
  equipmentLLCName: string;
  equipmentLLCEIN: string;
  equipmentLLCAddress: string;
  downPaymentAmount: number;
  cardNumber: string;
  cardName: string;
  cvv: string;
  business_owner: string;
  id: string;
  advisedBy?: string | null;
  advisor_confirmed?: string | null;
  profileReturning2025?: boolean | null;
  profileReturnYear2025?: number | null;
  formProgress?: string | null;
};
interface LeadStatus {
    id: string;
    profileReturning2025: boolean | null;
    profileReturnYear2025: number | null;
    formProgress: string | null;
    investmentAmount2025?: number | null;
}
interface Home2Props {
    user: any;
    formProgress: string; // Original prop name restored
    shouldShowReturningWizard: boolean;
    initialLeadStatus: LeadStatus | null;
}

// Original Constants & Functions
function getProgressValue(formProgress: string): number {
  const progressMapping: Record<string, number> = {
    'Step 0 Completed': 0,
    'Needs a follow-up': 50,
    'Using DLF for LLC formation': 100,
  };
  return progressMapping[formProgress] || 0;
}

export default function Home2(props: Home2Props) {
  const { user, formProgress: propFormProgress, shouldShowReturningWizard, initialLeadStatus } = props;
  const router = useRouter();
  const { toast } = useToast(); // Initialize toast

  // --- Original State Variables ---
  const [showCallout, setShowCallout] = useState(true);
  const [lead, setLead] = useState<Lead | null>(initialLeadStatus ? {
    // Copy existing properties from initialLeadStatus
    id: initialLeadStatus.id,
    profileReturning2025: initialLeadStatus.profileReturning2025,
    profileReturnYear2025: initialLeadStatus.profileReturnYear2025,
    formProgress: initialLeadStatus.formProgress,
    investmentAmount2025: initialLeadStatus.investmentAmount2025 || 0, // Use default if undefined
    // Add required properties from Lead type with default/empty values
    businessFoundingDate: null,
    expirationDate: null,
    firstName: "",
    middleName: "",
    lastName: "",
    streetAddress: "",
    postalCode: "",
    ssn: "",
    phone: "",
    email: "",
    state: "",
    city: "",
    country: "",
    advisorName: "",
    advisorEmail: "",
    isReturningInvestor: false,
    ownsEquipmentCompany: false,
    dlfEngagementForLLC: false,
    preferredBusinessName: "",
    alternateBusinessName: "",
    stateOfFormation: "",
    equipmentUsagePlan: "",
    investmentAmount: 0, // Default for the base investmentAmount
    downPaymentPercentage: 0,
    businessType: "",
    percentageOwnership: 0,
    equipmentLLCName: "",
    equipmentLLCEIN: "",
    equipmentLLCAddress: "",
    downPaymentAmount: 0,
    cardNumber: "",
    cardName: "",
    cvv: "",
    business_owner: "no", // Default business_owner
    advisedBy: null,
    advisor_confirmed: null
  } : null);
  const [showAdvisedByCallout, setShowAdvisedByCallout] = useState(false);
  const [advisorName, setAdvisorName] = useState("");
  const [showProfileModal, setShowProfileModal] = useState(false);

  // --- Wizard State ---
  const [wizardStep, setWizardStep] = useState<number>(1);
  const [isReturningSelection, setIsReturningSelection] = useState<'yes' | 'no' | null>(
    initialLeadStatus?.profileReturning2025 === false ? 'no' : 'yes'
  );
  const [returnYearSelection, setReturnYearSelection] = useState<2 | 3 | null>(
    initialLeadStatus?.profileReturnYear2025 === 3 ? 3 : 2
  );
  const [isSavingWizard, setIsSavingWizard] = useState<boolean>(false);
  const [investmentAmount2025, setInvestmentAmount2025] = useState<number | null>(
    initialLeadStatus?.investmentAmount2025 || null
  );

  // Agent-related state removed

  const [isMounted, setMounted] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  type LinkType = 'home' | 'theGPTlab' | 'calendar';
  const [linkType, setLinkType] = useState<LinkType>('home');
  const [messages, setMessages] = React.useState<{ type: string; message: string }[]>
    ([{ type: "ai", message: "Hey welcome to TPC Capital Solutions. How can I help?" }]);
  const [currentFormProgress, setCurrentFormProgress] = useState(initialLeadStatus?.formProgress || propFormProgress || 'Step 0 Completed');


  // --- Original useEffect Hooks ---
  useEffect(() => {
    setMounted(true);
    // Set initial wizard step
    if (shouldShowReturningWizard && initialLeadStatus) {
        if (initialLeadStatus.profileReturning2025 === true && initialLeadStatus.profileReturnYear2025) {
          // User has completed step 1 (selected yes and chose year) - show step 2
          setReturnYearSelection(initialLeadStatus.profileReturnYear2025 as 2 | 3);
          setIsReturningSelection('yes');
          setWizardStep(2);
          // Also restore investment amount if it exists
          if (initialLeadStatus.investmentAmount2025 !== null && initialLeadStatus.investmentAmount2025 !== undefined) {
            setInvestmentAmount2025(initialLeadStatus.investmentAmount2025);
          }
        }
        else if (initialLeadStatus.profileReturning2025 === false) {
          // User selected "not returning"
          setIsReturningSelection('no');
          setWizardStep(99);
        }
        else {
          // User hasn't completed step 1 yet
          setWizardStep(1);
        }
    }
    // Fetch Lead & Advisor Info (Original logic restored)
    const fetchLead = async () => {
        if (!user?.id) return;
        try {
            // Fetch the current lead for the logged-in user (server derives from session)
            const response = await axios.get(`/api/crm/leads`);
            if (response.status === 200 && response.data) {
                const fetchedLead: Lead = response.data;

                // Ensure date fields are properly formatted as Date objects
                if (fetchedLead.businessFoundingDate) {
                  fetchedLead.businessFoundingDate = new Date(fetchedLead.businessFoundingDate);
                }
                if (fetchedLead.expirationDate) {
                  fetchedLead.expirationDate = new Date(fetchedLead.expirationDate);
                }

                // Set investment amount state if available
                if (fetchedLead.investmentAmount2025) {
                  setInvestmentAmount2025(fetchedLead.investmentAmount2025);
                } else if (initialLeadStatus?.investmentAmount2025) {
                  setInvestmentAmount2025(initialLeadStatus.investmentAmount2025);
                }

                setLead(fetchedLead);
                if (fetchedLead.formProgress) setCurrentFormProgress(fetchedLead.formProgress);
                else if (initialLeadStatus?.formProgress) setCurrentFormProgress(initialLeadStatus.formProgress);


                if (fetchedLead.advisedBy) {
                    const advisorResponse = await axios.get(`/api/user/${fetchedLead.advisedBy}/name`);
                    if (advisorResponse.status === 200 && advisorResponse.data?.name) {
                        setAdvisorName(advisorResponse.data.name);
                        if (fetchedLead.advisor_confirmed !== 'confirmed') {
                          setShowAdvisedByCallout(true);
                        }
                    }
                }
            } else {
                // If full details not found, fallback to initialLeadStatus if available
                if(initialLeadStatus){
                   console.log("Full lead details not found, using initialLeadStatus to populate.");
                    // Create a minimal Lead object based on initialLeadStatus
                    const minimalLead: Lead = {
                        id: initialLeadStatus.id,
                        profileReturning2025: initialLeadStatus.profileReturning2025,
                        profileReturnYear2025: initialLeadStatus.profileReturnYear2025,
                        formProgress: initialLeadStatus.formProgress,
                        investmentAmount2025: initialLeadStatus.investmentAmount2025 || 0,
                        // Fill required fields with defaults
                        businessFoundingDate: null, expirationDate: null, firstName: "", middleName: "",
                        lastName: "", streetAddress: "", postalCode: "", ssn: "", phone: "", email: "", state: "",
                        city: "", country: "", advisorName: "", advisorEmail: "", isReturningInvestor: false,
                        ownsEquipmentCompany: false, dlfEngagementForLLC: false, preferredBusinessName: "",
                        alternateBusinessName: "", stateOfFormation: "", equipmentUsagePlan: "", investmentAmount: 0,
                        downPaymentPercentage: 0, businessType: "", percentageOwnership: 0, equipmentLLCName: "",
                        equipmentLLCEIN: "", equipmentLLCAddress: "", downPaymentAmount: 0, cardNumber: "",
                        cardName: "", cvv: "", business_owner: "no", advisedBy: null, advisor_confirmed: null
                    };
                    setLead(minimalLead);
                    setCurrentFormProgress(initialLeadStatus.formProgress || 'Step 0 Completed');
                    setInvestmentAmount2025(initialLeadStatus.investmentAmount2025 || 0);
                } else {
                  console.log("No lead details found and no initialLeadStatus provided.");
                  setLead(null);
                }
            }
        } catch (error: any) {
          if (error.response?.status === 404) {
             if(initialLeadStatus){
                   console.log("Full lead details not found (404), using initialLeadStatus.");
                    // Create a minimal Lead object based on initialLeadStatus
                    const minimalLead: Lead = {
                         id: initialLeadStatus.id,
                        profileReturning2025: initialLeadStatus.profileReturning2025,
                        profileReturnYear2025: initialLeadStatus.profileReturnYear2025,
                        formProgress: initialLeadStatus.formProgress,
                        investmentAmount2025: initialLeadStatus.investmentAmount2025 || 0,
                        businessFoundingDate: null, expirationDate: null, firstName: "", middleName: "",
                        lastName: "", streetAddress: "", postalCode: "", ssn: "", phone: "", email: "", state: "",
                        city: "", country: "", advisorName: "", advisorEmail: "", isReturningInvestor: false,
                        ownsEquipmentCompany: false, dlfEngagementForLLC: false, preferredBusinessName: "",
                        alternateBusinessName: "", stateOfFormation: "", equipmentUsagePlan: "", investmentAmount: 0,
                        downPaymentPercentage: 0, businessType: "", percentageOwnership: 0, equipmentLLCName: "",
                        equipmentLLCEIN: "", equipmentLLCAddress: "", downPaymentAmount: 0, cardNumber: "",
                        cardName: "", cvv: "", business_owner: "no", advisedBy: null, advisor_confirmed: null
                    };
                    setLead(minimalLead);
                    setCurrentFormProgress(initialLeadStatus.formProgress || 'Step 0 Completed');
                    setInvestmentAmount2025(initialLeadStatus.investmentAmount2025 || 0);
                } else {
                  console.log("No lead details found (404) and no initialLeadStatus.");
                  setLead(null);
                }
          } else {
            console.error("Error fetching lead details:", error);
             // Optionally fallback to initialLeadStatus on other errors too
             if(initialLeadStatus){ /* ... create minimal lead ... */ } else { setLead(null); }
          }
        }
    };
    fetchLead();
    return () => {
      setMounted(false);
    };
  }, [user?.id, shouldShowReturningWizard, initialLeadStatus]); // Rerun if initialLeadStatus changes





  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  // --- End Original useEffect Hooks ---

  // --- Wizard Logic Handlers ---
  const handleReturnSelection = async (selection: 'yes' | 'no') => {
    const leadIdToUpdate = initialLeadStatus?.id || lead?.id;
    if (!leadIdToUpdate) {
        toast({
            title: "Error",
            description: "Cannot save status: Lead ID missing.",
            variant: "destructive"
        });
        return;
    }

    const isReturningSelected = selection === 'yes'; // Convert to boolean for API

    setIsSavingWizard(true);
    try {
        // 1. Update the lead status in the database
        await axios.put(`/api/crm/leads/${leadIdToUpdate}/returning-status`, {
             isReturning: isReturningSelected // Send boolean
        });

        // Update local state if successful
        setIsReturningSelection(selection);
        setWizardStep(isReturningSelected ? 2 : 99);
        if (lead) setLead({...lead, profileReturning2025: isReturningSelected }); // Update local lead state

        toast({
            title: "Status Saved",
            description: "Your selection has been saved.",
            variant: "default"
        });

        // 2. Trigger the notification email (after successful status update)
        try {
            await axios.post('/api/notify/returning-investor', {
                leadId: leadIdToUpdate,
                isReturning: isReturningSelected // Pass the boolean value
            });
            console.log(`Notification sent for returning investor status (Lead: ${leadIdToUpdate}, Selection: ${selection})`);
        } catch (notifyError) {
            console.error("Error sending returning investor notification:", notifyError);
        }

    } catch (error) {
        console.error("Error saving returning status:", error);
        toast({
            title: "Error",
            description: "Could not save selection.",
            variant: "destructive"
        });
    } finally {
        setIsSavingWizard(false);
    }
};

  const handleYearSelection = async (year: 2 | 3) => {
    const leadIdToUpdate = initialLeadStatus?.id || lead?.id;
    if (!leadIdToUpdate) {
      toast({
        title: "Error",
        description: "Cannot save status: Lead ID missing.",
        variant: "destructive"
      });
      return;
    }
    setIsSavingWizard(true);
    try {
        await axios.put(`/api/crm/leads/${leadIdToUpdate}/returning-status`, { returnYear: year });
        setReturnYearSelection(year);
        setWizardStep(3);
         if (lead) setLead({...lead, profileReturnYear2025: year }); // Update local lead state

        toast({
          title: "Year Saved",
          description: "Your selection has been saved.",
          variant: "default"
        });
    } catch (error) {
      console.error("Error saving return year:", error);
      toast({
        title: "Error",
        description: "Could not save selection.",
        variant: "destructive"
      });
    } finally {
      setIsSavingWizard(false);
    }
  };

  // New handler for investment amount
  const handleInvestmentAmountSave = async () => {
    const leadIdToUpdate = initialLeadStatus?.id || lead?.id;
    if (!leadIdToUpdate) {
      toast({
        title: "Error",
        description: "Cannot save investment amount: Lead ID missing.",
        variant: "destructive"
      });
      return;
    }

    if (investmentAmount2025 === undefined || investmentAmount2025 === null || investmentAmount2025 < 0) { // Allow 0
        toast({
            title: "Error",
            description: "Please enter a valid investment amount (0 or greater).",
            variant: "destructive"
        });
        return;
    }


    setIsSavingWizard(true);
    try {
        // Call API to save investment amount
        await axios.put(`/api/crm/leads/${leadIdToUpdate}/investment-amount`, {
          investmentAmount2025: investmentAmount2025
        });

        // Update local lead data
        if (lead) {
          setLead({
            ...lead,
            investmentAmount2025: investmentAmount2025
          });
        }

        toast({
          title: "Success",
          description: "Investment amount has been saved.",
          variant: "default"
        });
    } catch (error) {
      console.error("Error saving investment amount:", error);
      toast({
        title: "Error",
        description: "Could not save investment amount.",
        variant: "destructive"
      });
    } finally {
      setIsSavingWizard(false);
    }
  };
  // --- End Wizard Logic Handlers ---

  // --- Original Handlers (Unchanged logic) ---
  const handleAdvisedByButtonClick = async (continueWithAdvisor: boolean) => {
    const leadIdToUpdate = initialLeadStatus?.id || lead?.id;
    if (!leadIdToUpdate) {
      console.error("Cannot confirm advisor - Lead ID missing");
      toast({
        title: "Error",
        description: "Lead information missing.",
        variant: "destructive"
      });
      return;
    }
    setShowAdvisedByCallout(false);
    if (continueWithAdvisor) {
      try {
        await axios.put(`/api/crm/leads/${leadIdToUpdate}/advisor-confirmed`, { advisor_confirmed: "confirmed" });
        console.log("Lead advisor confirmed");
        setLead(prev => prev ? { ...prev, advisor_confirmed: "confirmed" } : null);
      } catch (error) {
        console.error("Error confirming advisor:", error);
      }
    } else {
      console.log("User chose not to continue with advisor.");
    }
  };

  const handleButtonClick = async (isAdvisor: boolean) => {
    setShowCallout(false);
    if (isAdvisor) {
      try {
        await axios.put(`/api/user/${user.id}/updateRole`, { role: 'user' });
        router.refresh();
      } catch (error) {
        console.error('Error updating user role:', error);
      }
    }
  };

  const handleToggleLink = (data: FunctionCallData) => {
    /* original logic */
    console.log('Function call:', data);
    const newLinkType = data.args.toggle;
    if (newLinkType) setLinkType(newLinkType as LinkType);
    else console.error('No link type specified');
  };



  // --- Original Steps Definition (Unchanged) ---
  const steps = [
    {
      id: 1,
      type: 'in progress',
      title: 'Investor Profile Intake Form',
      description: 'Fill out the Investor Profile Intake Form. This is crucial for due diligence and loan application.',
      href: '/personal-info',
    },
    {
      id: 2,
      type: 'in progress',
      title: 'Profile Management & Document signing',
      description: 'Manage your personal and entity information, and track status of the process. If you plan to setup the entity through DLF you can sign documents here.',
      href: lead?.id ? `/leads/${lead.id}` : '/leads', // Use state lead id if available
    },
  ];
  // --- End Original Steps Definition ---

  // Format the investment amount with commas
  const formatCurrency = (amount: number | null) => {
    // Handle null or undefined gracefully
    if (amount === null || amount === undefined) {
        return ''; // Return empty string instead of $0
    }
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Use derived form progress state for display
  const displayFormProgress = currentFormProgress; // Use state variable

  if (!isMounted) {
    return null; // Or a loading spinner
  }

  return (
    <>
      {/* Profile Update Modal */}
      <RightViewModalNoTrigger
        title="Update Investor Profile"
        description="Update your investor profile information"
        open={showProfileModal}
        setOpen={setShowProfileModal}
      >
        {/* Ensure lead is not null before rendering the form */}
        {lead ? (
          <UpdateLeadForm
            // Pass the full lead state, ensuring defaults are handled if some fields are missing initially
            initialData={{
              ...lead,
              // Map potentially missing fields to defaults expected by UpdateLeadForm
              businessFoundingDate: lead.businessFoundingDate ? new Date(lead.businessFoundingDate) : new Date(),
              expirationDate: lead.expirationDate ? new Date(lead.expirationDate) : new Date(),
              first_name: lead.firstName || "",
              middle_name: lead.middleName || "",
              last_name: lead.lastName || "",
              street_address: lead.streetAddress || "",
              postal_code: lead.postalCode || "",
              ssn: lead.ssn || "",
              phone: lead.phone || "",
              email: lead.email || "",
              state: lead.state || "",
              city: lead.city || "",
              country: lead.country || "",
              advisorName: lead.advisorName || "",
              advisorEmail: lead.advisorEmail || "",
              isReturningInvestor: !!lead.isReturningInvestor,
              ownsEquipmentCompany: !!lead.ownsEquipmentCompany,
              dlfEngagementForLLC: !!lead.dlfEngagementForLLC,
              preferredBusinessName: lead.preferredBusinessName || "",
              alternateBusinessName: lead.alternateBusinessName || "",
              stateOfFormation: lead.stateOfFormation || "",
              equipmentUsagePlan: lead.equipmentUsagePlan || "",
              investmentAmount: lead.investmentAmount ?? 0, // Use nullish coalescing
              investmentAmount2025: lead.investmentAmount2025 ?? undefined, // Pass undefined if null/undefined
              downPaymentPercentage: lead.downPaymentPercentage ?? 0,
              businessType: lead.businessType || "",
              percentageOwnership: lead.percentageOwnership ?? 0,
              equipmentLLCName: lead.equipmentLLCName || "",
              equipmentLLCEIN: lead.equipmentLLCEIN || "",
              equipmentLLCAddress: lead.equipmentLLCAddress || "",
              downPaymentAmount: lead.downPaymentAmount ?? 0,
              cardNumber: lead.cardNumber || "",
              cardName: lead.cardName || "",
              cvv: lead.cvv || "",
              business_owner: lead.business_owner === "yes" ? "yes" : "no", // Ensure it's 'yes' or 'no'
               // Pass other potential fields from Lead that UpdateLeadForm might use
              advisedBy: lead.advisedBy,
              advisor_confirmed: lead.advisor_confirmed,
              profileReturning2025: lead.profileReturning2025,
              profileReturnYear2025: lead.profileReturnYear2025,
              formProgress: lead.formProgress,
            }}
            setOpen={setShowProfileModal}
          />
        ) : (
            <Text>Loading profile data...</Text> // Show loading or placeholder if lead is null
        )}
      </RightViewModalNoTrigger>

      <main className="flex overflow-y-auto flex-col h-full">
        <div className="p-6 w-full md:p-8">
        <div className="mx-auto max-w-4xl">
          {/* Returning Investor Wizard - Prominent Center Placement */}
          {shouldShowReturningWizard && (
            <div className="mb-8">
              <Card className="border-2 border-red-500 shadow-lg">
                <div className="p-6 md:p-8">
                  {/* Wizard Step 1 - Combined All Questions */}
                  {wizardStep === 1 && (
                    <>
                      {/* Check if lead ID is missing and show appropriate callout */}
                      {(!initialLeadStatus?.id && !lead?.id) ? (
                        <Callout 
                          title="⚠️ IMPORTANT: Complete Your Investor Profile First" 
                          icon={RiAlarmWarningLine} 
                          color="red"
                        >
                          <Text className="mt-2 font-semibold">
                            You must complete your Investor Profile Intake Form before proceeding with the returning investor check.
                          </Text>
                          <div className="mt-4">
                            <Button
                              size="sm"
                              className="text-white bg-red-600 hover:bg-red-700"
                              onClick={() => router.push('/personal-info')}
                            >
                              Fill Out Investor Profile Now
                            </Button>
                          </div>
                        </Callout>
                      ) : (
                        <>
                          <Callout 
                            title="🚨 RETURNING INVESTOR CHECK - ACTION REQUIRED" 
                            color="red"
                            className="mb-6"
                          >
                            <Text className="font-semibold">
                              Please complete all fields below to continue with your 2025 investment.
                            </Text>
                          </Callout>
                          
                          <div className="space-y-6">
                            {/* Question 1: Returning Investor */}
                            <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
                              <Title className="text-red-900">1. Are you a returning investor?</Title>
                              <Text className="mt-2 text-red-800">
                                Are you intending to participate in the 2025 financing season?
                              </Text>
                              <div className="flex mt-4 space-x-3">
                                <Button
                                  size="sm"
                                  variant={isReturningSelection === 'yes' ? 'primary' : 'secondary'}
                                  className={isReturningSelection === 'yes' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                  onClick={() => setIsReturningSelection('yes')}
                                  disabled={isSavingWizard}
                                >
                                  Yes, I&apos;m Returning
                                </Button>
                                <Button
                                  size="sm"
                                  variant={isReturningSelection === 'no' ? 'primary' : 'secondary'}
                                  className={isReturningSelection === 'no' ? 'bg-gray-600 hover:bg-gray-700 text-white' : ''}
                                  onClick={() => setIsReturningSelection('no')}
                                  disabled={isSavingWizard}
                                >
                                  No, I&apos;m Not Returning
                                </Button>
                              </div>
                            </div>

                            {/* Question 2: Investment Year - Always show when returning */}
                            {isReturningSelection === 'yes' && (
                              <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                                <Title className="text-blue-900">2. Investment Year</Title>
                                <Text className="mt-2 text-blue-800">
                                  Is this your second or third year investing with TPC?
                                </Text>
                                <div className="flex mt-4 space-x-3">
                                  <Button
                                    size="sm"
                                    variant={returnYearSelection === 2 ? 'primary' : 'secondary'}
                                    className={returnYearSelection === 2 ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                                    onClick={() => setReturnYearSelection(2)}
                                    disabled={isSavingWizard}
                                  >
                                    2nd Year
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={returnYearSelection === 3 ? 'primary' : 'secondary'}
                                    className={returnYearSelection === 3 ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                                    onClick={() => setReturnYearSelection(3)}
                                    disabled={isSavingWizard}
                                  >
                                    3rd Year
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Question 3: Investment Amount - Make more prominent */}
                            {isReturningSelection === 'yes' && (
                              <div className="p-6 bg-purple-50 rounded-lg border-4 border-purple-300 shadow-lg">
                                <Title className="text-purple-900 text-xl">3. 2025 Investment Amount</Title>
                                <Text className="mt-2 text-purple-800 font-semibold">
                                  Please specify your intended investment amount for the 2025 financing season:
                                </Text>
                                <div className="flex items-center mt-4 space-x-2">
                                  <div className="relative flex-1 max-w-sm">
                                    <span className="flex absolute inset-y-0 left-3 items-center text-purple-600 text-lg font-bold">$</span>
                                    <Input
                                      type="number"
                                      value={investmentAmount2025 ?? ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setInvestmentAmount2025(value === '' ? null : parseInt(value) || 0);
                                      }}
                                      className="pr-3 pl-8 text-lg border-2 border-purple-300 focus:border-purple-500"
                                      placeholder="Enter amount"
                                      min="0"
                                      disabled={isSavingWizard}
                                    />
                                  </div>
                                </div>
                                {lead?.investmentAmount2025 !== undefined && 
                                 lead?.investmentAmount2025 !== null && 
                                 lead.investmentAmount2025 > 0 && (
                                  <Text className="mt-2 text-sm text-purple-700">
                                    Current saved amount: {formatCurrency(lead.investmentAmount2025)}
                                  </Text>
                                )}
                              </div>
                            )}

                            {/* Submit Button - Show when all required fields are filled */}
                            <div className="flex justify-center mt-6">
                              {isReturningSelection === 'no' ? (
                                <Button
                                  size="lg"
                                  className="px-8 text-white bg-red-600 hover:bg-red-700"
                                  onClick={() => handleReturnSelection('no')}
                                  loading={isSavingWizard}
                                  disabled={isSavingWizard}
                                >
                                  Submit - Not Returning for 2025
                                </Button>
                              ) : (
                                isReturningSelection === 'yes' && returnYearSelection && (
                                  <Button
                                    size="lg"
                                    className="px-8 text-white bg-red-600 hover:bg-red-700"
                                    onClick={async () => {
                                      // Save all data at once
                                      const leadIdToUpdate = initialLeadStatus?.id || lead?.id;
                                      if (!leadIdToUpdate) {
                                        toast({
                                          title: "Error",
                                          description: "Cannot save status: Lead ID missing.",
                                          variant: "destructive"
                                        });
                                        return;
                                      }

                                      setIsSavingWizard(true);
                                      try {
                                        // Save returning status
                                        await axios.put(`/api/crm/leads/${leadIdToUpdate}/returning-status`, {
                                          isReturning: true,
                                          returnYear: returnYearSelection
                                        });

                                        // Save investment amount if provided
                                        if (investmentAmount2025 !== null && investmentAmount2025 >= 0) {
                                          await axios.put(`/api/crm/leads/${leadIdToUpdate}/investment-amount`, {
                                            investmentAmount2025: investmentAmount2025
                                          });
                                        }

                                        // Update local state
                                        if (lead) {
                                          setLead({
                                            ...lead,
                                            profileReturning2025: true,
                                            profileReturnYear2025: returnYearSelection,
                                            investmentAmount2025: investmentAmount2025 ?? undefined
                                          });
                                        }

                                        setWizardStep(2);

                                        toast({
                                          title: "Success",
                                          description: "Your information has been saved.",
                                          variant: "default"
                                        });

                                        // Trigger notification email
                                        try {
                                          await axios.post('/api/notify/returning-investor', {
                                            leadId: leadIdToUpdate,
                                            isReturning: true
                                          });
                                        } catch (notifyError) {
                                          console.error("Error sending returning investor notification:", notifyError);
                                        }
                                      } catch (error) {
                                        console.error("Error saving data:", error);
                                        toast({
                                          title: "Error",
                                          description: "Could not save your information.",
                                          variant: "destructive"
                                        });
                                      } finally {
                                        setIsSavingWizard(false);
                                      }
                                    }}
                                    loading={isSavingWizard}
                                    disabled={isSavingWizard || (isReturningSelection === 'yes' && investmentAmount2025 === null)}
                                  >
                                    Continue to Next Step
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Wizard Step 2 - Document Updates */}
                  {wizardStep === 2 && (
                    <>
                      <Callout 
                        title="📄 DOCUMENT UPDATE REQUIRED" 
                        color="red"
                        className="mb-6"
                      >
                        <Text className="font-semibold">
                          As a returning investor, you must upload updated documents for the 2025 season.
                        </Text>
                      </Callout>

                      <Title className="text-red-900">Next Steps for Returning Investors</Title>

                      {/* 1. Update Profile Section */}
                      <div className="p-4 mt-6 bg-red-50 rounded-lg border-2 border-red-200">
                        <Text className="font-semibold text-red-900">
                          1. Update Your Profile
                        </Text>
                        <Text className="mt-1 text-red-800">
                          Please review and update your investor profile information if anything has changed since last year.
                        </Text>
                        <div className="mt-3">
                          <Button
                            size="sm"
                            className="text-white bg-red-600 hover:bg-red-700"
                            onClick={() => setShowProfileModal(true)}
                            disabled={!lead}
                          >
                            Update Profile Information
                          </Button>
                        </div>
                      </div>

                      {/* 2. Upload Documents Section */}
                      <div className="p-4 mt-6 bg-red-50 rounded-lg border-2 border-red-200">
                        <Text className="font-semibold text-red-900">
                          2. Upload 2025 Documents
                        </Text>
                        <Text className="mt-1 text-red-800">
                          Please upload the following documents for the 2025 season:
                        </Text>
                        <ul className="pl-5 mt-2 space-y-1 list-disc text-red-800">
                          <li className="font-semibold">2024 tax return & K1s</li>
                          <li className="font-semibold">Personal Financial statement (not older than 3 months)</li>
                        </ul>
                        <div className="mt-3">
                          <Button
                            size="sm"
                            className="text-white bg-red-600 hover:bg-red-700"
                            onClick={() => router.push(lead?.id ? `/leads/` : initialLeadStatus?.id ? `/leads/`: '/leads')}
                            disabled={!lead && !initialLeadStatus}
                          >
                            Upload Documents Now
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Wizard Step 99 - Not Returning Confirmation */}
                  {wizardStep === 99 && (
                    <>
                      <Title className="text-red-900">Confirmation</Title>
                      <Text className="mt-2 text-red-800">
                        Thank you for letting us know. If you change your mind or have questions, please contact support.
                      </Text>
                    </>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Original Content */}
          <h3 className="font-semibold text-tremor-title text-tremor-content-strong">Hello</h3>
          <p className="text-tremor-default text-tremor-content">
            Embark on your equipment financing journey with TPC Capital Solutions.
          </p>

          {/* Callouts */}
          <div className="mt-6 space-y-4">
            {lead && lead.advisedBy && showAdvisedByCallout && (
              <Callout title={`Is ${advisorName} your financial advisor?`} color="blue">
                <div className="flex justify-end mt-4 space-x-4">
                  <Button size="sm" onClick={() => handleAdvisedByButtonClick(true)}>Yes</Button>
                  <Button size="sm" variant="secondary" onClick={() => handleAdvisedByButtonClick(false)}>No</Button>
                </div>
              </Callout>
            )}
            {/* Only show advisor callout if lead data isn't loaded or if lead isn't associated with an advisor yet */}
            {(!lead || !lead.advisedBy) && showCallout && (
              <Callout title="Are you a registered Advisor?" color="blue">
                <div className="flex justify-end mt-4 space-x-4">
                  <Button size="sm" onClick={() => handleButtonClick(true)}>Yes</Button>
                  <Button size="sm" variant="secondary" onClick={() => handleButtonClick(false)}>No</Button>
                </div>
              </Callout>
            )}
          </div>

          {/* Steps List */}
          <ul role="list" className="mt-4 space-y-3">
            {steps.map((step) => (
              <li key={step.id} className="relative p-4 bg-white rounded-lg border shadow-sm hover:bg-gray-50">
                <div className="flex items-start">
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="font-medium leading-5 text-tremor-content-strong">
                      <a href={step.href} className="focus:outline-none">
                        <span className="absolute inset-0" aria-hidden="true" />
                        {step.title}
                      </a>
                    </p>
                    <p className="mt-1 mb-2 leading-6 text-tremor-default text-tremor-content">
                      {step.description}
                    </p>

                    {/* Callouts within Steps */}
                    {step.id === 1 && (
                      <div className="flex justify-left">
                        {displayFormProgress === 'Using DLF for LLC formation' && (
                          <Callout title="Entity Formation in Progress" icon={RiAlarmWarningLine} color="blue">
                            Your entity is currently being formed...
                          </Callout>
                        )}
                        {displayFormProgress === 'Needs a follow-up' && (
                          <Callout title="Entity Formation in Progress" icon={RiAlarmWarningLine} color="blue">
                            Someone from our team will follow up...
                          </Callout>
                        )}
                        {displayFormProgress !== 'Using DLF for LLC formation' && displayFormProgress !== 'Needs a follow-up' && (
                          <Callout title="Click to Get Started" icon={RiAlarmWarningLine} color="gray">
                            To begin your equipment financing journey...
                          </Callout>
                        )}
                      </div>
                    )}
                    {step.id === 2 && (
                      <div className="flex justify-left">
                        <Callout title="Once your documents are uploaded..." icon={RiAlarmWarningLine} color="teal">
                          Manage profile details and view/sign documents here.
                        </Callout>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </main>
    </>
  );
}
